import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Download,
  Mic,
  ArrowLeft,
  Calendar,
  Building2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useUploadResults,
  useRecommendations,
} from '@/api/hooks/useBloodwork';
import type {
  ExtractedMarker,
  FlaggedMarker,
} from '@/api/hooks/useBloodwork';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BiomarkerScorecard } from '@/components/bloodwork/BiomarkerScorecard';
import { BiomarkerCard } from '@/components/bloodwork/BiomarkerCard';
import type { BiomarkerMarker } from '@/components/bloodwork/BiomarkerCard';
import { TierBadge } from '@/components/bloodwork/TierBadge';
import { ResultsSummary } from '@/components/bloodwork/ResultsSummary';
import { RecommendationCard } from '@/components/bloodwork/RecommendationCard';
import { AnalysisProgress } from '@/components/bloodwork/AnalysisProgress';
import {
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  CATEGORY_ORDER,
} from '@/lib/biomarkers';

// Animation variants for stagger
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' as const },
  },
};

/**
 * Merge extracted markers with flagged marker data.
 * Flagged markers contain research_context and pmids.
 */
function mergeMarkers(
  extracted: ExtractedMarker[],
  flagged: FlaggedMarker[]
): BiomarkerMarker[] {
  const flaggedMap = new Map(flagged.map((f) => [f.key, f]));

  return extracted.map((m) => {
    const f = flaggedMap.get(m.key);
    return {
      key: m.key,
      name: m.name,
      value: m.value,
      unit: m.unit,
      optimalLow: m.optimal_low ?? m.ref_low ?? 0,
      optimalHigh: m.optimal_high ?? m.ref_high ?? 100,
      refLow: m.ref_low,
      refHigh: m.ref_high,
      status: m.status,
      researchContext: f?.research_context,
      pmids: f?.pmids,
    };
  });
}

/**
 * Group markers by category, preserving CATEGORY_ORDER.
 */
function groupByCategory(
  markers: BiomarkerMarker[]
): Array<{ category: string; label: string; markers: BiomarkerMarker[] }> {
  const groups = new Map<string, BiomarkerMarker[]>();

  for (const m of markers) {
    const cat =
      (m as BiomarkerMarker & { category?: string }).category ||
      extractCategory(m.key);
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push(m);
  }

  // Order by CATEGORY_ORDER, then any remaining
  const ordered: Array<{
    category: string;
    label: string;
    markers: BiomarkerMarker[];
  }> = [];

  for (const cat of CATEGORY_ORDER) {
    const marks = groups.get(cat);
    if (marks && marks.length > 0) {
      ordered.push({
        category: cat,
        label: CATEGORY_LABELS[cat] || cat,
        markers: marks,
      });
      groups.delete(cat);
    }
  }

  // Remaining categories
  for (const [cat, marks] of groups) {
    ordered.push({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      markers: marks,
    });
  }

  return ordered;
}

/** Best-effort category extraction from marker key if not on the marker itself */
function extractCategory(_key: string): string {
  return 'other';
}

function ResultsSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6 lg:p-8">
      <Skeleton className="h-10 w-64" />
      <div className="flex gap-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-24" />
      </div>
      <Skeleton className="h-[400px] w-full rounded-xl" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export function ResultsPage() {
  const { id } = useParams<{ id: string }>();
  const { data: resultData, isLoading: resultsLoading, error: resultsError } =
    useUploadResults(id);
  const { data: recData, isLoading: recsLoading } = useRecommendations(id);

  const result = resultData?.result;
  const recommendations = recData?.recommendations ?? [];

  // If still processing, show progress
  if (result && result.status === 'processing') {
    // Estimate stage from status metadata
    const stage = result.extracted_markers?.length > 0 ? 2 : 1;
    return <AnalysisProgress stage={stage} />;
  }

  if (resultsLoading) {
    return <ResultsSkeleton />;
  }

  if (resultsError || !result) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-navy">
            Analysis not found
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            The requested results could not be loaded. They may still be
            processing.
          </p>
          <Link to="/">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Merge extracted and flagged markers, add category from extracted data
  const allMarkers = mergeMarkers(
    result.extracted_markers ?? [],
    result.flagged_markers ?? []
  );
  // Attach category to each marker from extracted_markers
  const markersWithCategory = allMarkers.map((m, i) => ({
    ...m,
    category: result.extracted_markers?.[i]?.category ?? 'other',
  }));
  const grouped = groupByCategory(markersWithCategory);

  // Sort recommendations by priority (lower number = higher priority), grouped by type
  const sortedRecs = [...recommendations].sort(
    (a, b) => a.priority - b.priority
  );
  const supplementRecs = sortedRecs.filter((r) => r.type === 'supplement');
  const lifestyleRecs = sortedRecs.filter((r) => r.type === 'lifestyle');
  const clinicalRecs = sortedRecs.filter((r) => r.type === 'clinical');
  const retestRecs = sortedRecs.filter((r) => r.type === 'retest');
  const orderedRecs = [
    ...supplementRecs,
    ...lifestyleRecs,
    ...clinicalRecs,
    ...retestRecs,
  ];

  return (
    <motion.div
      className="mx-auto max-w-5xl space-y-10 p-6 lg:p-8"
      variants={container}
      initial="hidden"
      animate="show"
    >
      {/* Back link */}
      <motion.div variants={item}>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>
      </motion.div>

      {/* ─── Header ─── */}
      <motion.div variants={item}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy">
              Bloodwork Analysis
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
              {result.lab_date && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(result.lab_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              )}
              {result.lab_provider && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {result.lab_provider}
                </span>
              )}
            </div>
            <div className="mt-3">
              <TierBadge tier={result.tier} size="lg" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled>
              <Download className="mr-1 h-4 w-4" />
              Download PDF
            </Button>
            <a
              href={`https://mensresearch.ai/expert?slug=performance&uploadId=${id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm">
                <Mic className="mr-1 h-4 w-4" />
                Talk to Research Expert
              </Button>
            </a>
          </div>
        </div>
      </motion.div>

      {/* ─── Radar Chart ─── */}
      {result.category_scores &&
        Object.keys(result.category_scores).length > 0 && (
          <motion.section variants={item}>
            <h2 className="mb-4 text-lg font-semibold text-navy">
              Your Category Scores
            </h2>
            <Card>
              <CardContent>
                <BiomarkerScorecard
                  categoryScores={result.category_scores}
                />
              </CardContent>
            </Card>
          </motion.section>
        )}

      {/* ─── Summary ─── */}
      {result.summary && (
        <motion.section variants={item}>
          <h2 className="mb-4 text-lg font-semibold text-navy">
            Analysis Summary
          </h2>
          <ResultsSummary summary={result.summary} tier={result.tier} />
        </motion.section>
      )}

      {/* ─── Biomarker Details ─── */}
      {grouped.length > 0 && (
        <motion.section variants={item}>
          <h2 className="mb-6 text-lg font-semibold text-navy">
            Biomarker Details
          </h2>
          <div className="space-y-8">
            {grouped.map((group) => {
              const categoryColor =
                CATEGORY_COLORS[group.category] || '#3d5a80';
              const categoryScore =
                result.category_scores?.[group.category];

              return (
                <motion.div key={group.category} variants={item}>
                  {/* Category heading */}
                  <div className="mb-3 flex items-center gap-3">
                    <div
                      className="h-1 w-6 rounded-full"
                      style={{ backgroundColor: categoryColor }}
                    />
                    <h3 className="text-base font-semibold text-navy">
                      {group.label}
                    </h3>
                    {categoryScore != null && (
                      <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
                        style={{ backgroundColor: categoryColor }}
                      >
                        {categoryScore}%
                      </span>
                    )}
                  </div>

                  {/* Marker cards */}
                  <div className="space-y-2">
                    {group.markers.map((marker) => (
                      <BiomarkerCard key={marker.key} marker={marker} />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>
      )}

      {/* ─── Recommendations ─── */}
      {(orderedRecs.length > 0 || recsLoading) && (
        <motion.section variants={item}>
          <h2 className="mb-4 text-lg font-semibold text-navy">
            Your Recommendations
          </h2>
          {recsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {orderedRecs.map((rec) => (
                <motion.div key={rec.id} variants={item}>
                  <RecommendationCard rec={rec} />
                </motion.div>
              ))}
            </div>
          )}
        </motion.section>
      )}

      {/* ─── Voice CTA ─── */}
      <motion.section variants={item}>
        <Card className="border-copper/20 bg-gradient-to-br from-copper/5 to-transparent">
          <CardContent>
            <div className="flex flex-col items-center gap-4 py-4 text-center sm:flex-row sm:text-left">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-copper/10">
                <Mic className="h-8 w-8 text-copper" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-navy">
                  Want me to walk you through your results?
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Ask questions about any of your results. Get answers
                  grounded in the actual research.
                </p>
              </div>
              <a
                href={`https://mensresearch.ai/expert?slug=performance&uploadId=${id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" className="whitespace-nowrap">
                  <Mic className="mr-2 h-5 w-5" />
                  Talk to Research Expert
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </motion.section>
    </motion.div>
  );
}
